import { useEffect, useState, useRef } from "react";
import api, { errMsg, postUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SOLAR_VIC_STATUS, DISTRIBUTOR_POST_INSTALL, PREPOST_EDIT_ROLES, QUOTE_TYPES, currency, fmtDate } from "../lib/constants";
import TaxInvoicePanel from "./TaxInvoicePanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div><label className="label-caps">{label}</label>{children}</div>
);
const inputCls = "w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm disabled:opacity-60";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/40 last:border-0 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="font-medium text-right [overflow-wrap:anywhere]">{value || "—"}</span>
  </div>
);

const TickButton = ({ active, onClick, disabled, label, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 ${
      active ? "bg-emerald-600 text-white" : "border border-input hover:bg-muted"}`}>
    {active ? <Icons.CheckCircle2 size={16} /> : <Icons.Circle size={16} />} {label}
  </button>
);

export default function PrepostPanel({ lead, quotes = [], onRefresh, onOpenChat }) {
  const { user } = useAuth();
  const canEdit = PREPOST_EDIT_ROLES.includes(user?.role) && !(lead?.prepost_locked_for_sales && !["super_admin", "admin", "sales_manager", "admin_manager", "prepost"].includes(user?.role));
  const editable = PREPOST_EDIT_ROLES.includes(user?.role);
  const prop = lead.property_details || {};
  const signedQuote = quotes.find((q) => q.signed);
  const [f, setF] = useState({
    solar_vic_status: "", solar_vic_ins_no: "", dnsp_preapproval_ref: "", dnsp_export_limit: "",
    stock_delivery_booked: "", install_booked_date: "", distributor_post_install_status: "",
    retailer_notified: false, post_install_ref: "", include_vic_rebate: true, include_vic_loan: true,
  });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [slip, setSlip] = useState({ text: "", can_send: false, detail: "" });
  const [sendingSlip, setSendingSlip] = useState(false);
  const [jobPackOpen, setJobPackOpen] = useState(false);
  const [jobPackMode, setJobPackMode] = useState("send");
  const [photoCheck, setPhotoCheck] = useState(null);
  const notEligible = f.solar_vic_status === "Not eligible";

  useEffect(() => {
    api.get(`/leads/${lead.id}/photo-checklist`).then((r) => setPhotoCheck(r.data)).catch(() => setPhotoCheck(null));
  }, [lead.id]);

  useEffect(() => {
    setF({
      solar_vic_status: lead.solar_vic_status || "",
      solar_vic_ins_no: lead.solar_vic_ins_no || "",
      dnsp_preapproval_ref: lead.dnsp_preapproval_ref || "",
      dnsp_export_limit: lead.dnsp_export_limit || "",
      stock_delivery_booked: lead.stock_delivery_booked || "",
      install_booked_date: lead.install_booked_date || "",
      distributor_post_install_status: lead.distributor_post_install_status || "",
      retailer_notified: !!lead.retailer_notified,
      post_install_ref: lead.post_install_ref || "",
      include_vic_rebate: lead.include_vic_rebate !== false,
      include_vic_loan: lead.include_vic_loan !== false,
    });
  }, [lead]);

  useEffect(() => {
    api.get(`/leads/${lead.id}/packing-slip`).then((r) => setSlip(r.data)).catch(() => setSlip({ text: "", can_send: false }));
  }, [lead.id, lead.stage]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (extra = {}) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}/prepost`, { ...f, ...extra });
      toast.success("Prepost details saved");
      await onRefresh?.();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
    savingRef.current = false;
  };

  const copySlip = () => { navigator.clipboard?.writeText(slip.text); toast.success("Packing slip copied"); };
  const sendSlip = async () => {
    setSendingSlip(true);
    try {
      await api.post(`/leads/${lead.id}/send-packing-slip`);
      toast.success("Packing slip emailed to info@griansolarpower.com.au");
      onRefresh?.();
    } catch (e) { toast.error(errMsg(e)); }
    setSendingSlip(false);
  };

  return (
    <div className="space-y-4" data-testid="prepost-panel">
      {!editable && <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3" data-testid="prepost-readonly-note">Read-only — the Pre/Post section is managed by Pre/Post officers and admins.</div>}
      {lead.prepost_locked_for_sales && <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md p-3" data-testid="prepost-finalized-note">This project is Won — Finalized and locked for Sales.</div>}

      {/* Customer + property overview (read-only, everything in one place) */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-customer">
          <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icons.User size={16} className="text-secondary" /> Customer</h3>
          <Row label="Name" value={`${lead.first_name || ""} ${lead.last_name || ""}`.trim()} />
          <Row label="Phone" value={lead.phone} />
          <Row label="Email" value={lead.email} />
          <Row label="Address" value={lead.full_address} />
          {(lead.alt_contact_name || lead.alt_contact_phone) && <Row label="Alt contact" value={`${lead.alt_contact_name || ""} ${lead.alt_contact_phone || ""}`.trim()} />}
        </div>
        <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-property-summary">
          <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icons.Home size={16} className="text-secondary" /> Property details</h3>
          <Row label="Property type" value={prop.property_type} />
          <Row label="Stories" value={prop.no_of_stories} />
          <Row label="Roof type" value={prop.roof_type} />
          <Row label="Roof pitch" value={prop.roof_pitch} />
          <Row label="Tilt" value={prop.tilt_requirement} />
          <Row label="Phase" value={prop.phase} />
          <Row label="Switchboard" value={prop.switchboard} />
          <Row label="Distributor" value={prop.distributor} />
          <Row label="Retailer" value={prop.retailer} />
          <Row label="NMI No." value={prop.nmi_no} />
          <Row label="Meter No." value={prop.meter_no} />
        </div>
      </div>

      {/* Signed quote summary with out-of-pocket */}
      {signedQuote && (
        <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-signed-quote">
          <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icons.FileSignature size={16} className="text-secondary" /> Signed quote</h3>
          <div className="grid sm:grid-cols-2 gap-x-6">
            <Row label="Quote No." value={signedQuote.quote_no} />
            <Row label="System" value={QUOTE_TYPES.find((x) => x.key === signedQuote.quote_type)?.label} />
            <Row label="Out of pocket" value={currency(signedQuote.out_of_pocket)} />
            {signedQuote.signed_at && <Row label="Signed" value={fmtDate(signedQuote.signed_at)} />}
          </div>
        </div>
      )}

      {/* Packing slip */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-packing-slip">
        <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.PackageCheck size={16} className="text-secondary" /> Packing slip</h3>
        {slip.can_send ? (
          <>
            <p className="text-xs text-muted-foreground mb-3">Auto-built from the signed quote. Copy it, or send it straight to info@griansolarpower.com.au.</p>
            <textarea readOnly value={slip.text} rows={10} data-testid="packing-slip-text"
              className="w-full px-3 py-2 rounded-md border border-input bg-muted/30 text-sm font-mono whitespace-pre [overflow-wrap:normal] overflow-x-auto" />
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={copySlip} data-testid="packing-slip-copy" className="px-4 py-2 rounded-md border border-border text-sm font-semibold flex items-center gap-1.5"><Icons.Copy size={14} /> Copy</button>
              {editable && (
                <button onClick={sendSlip} disabled={sendingSlip} data-testid="packing-slip-send" className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60 flex items-center gap-1.5">
                  <Icons.Send size={14} /> {sendingSlip ? "Sending…" : "Send Packing Slip"}</button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="packing-slip-unavailable">{slip.detail || "Available once the customer has signed a quote."}</p>
        )}
      </div>

      {/* Site photo checklist */}
      {photoCheck && photoCheck.total > 0 && (
        <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-photo-checklist">
          <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.Camera size={16} className="text-secondary" /> Site Photos
            <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${photoCheck.missing.length === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="photo-checklist-count">{photoCheck.present}/{photoCheck.total}</span>
          </h3>
          <p className="text-xs text-muted-foreground mb-3">{photoCheck.missing.length === 0 ? "All required site photos are uploaded." : "Upload the missing photos before sending the Job Pack."}</p>
          <ul className="space-y-1.5">
            {photoCheck.items.map((it) => (
              <li key={it.label} className="flex items-center gap-2 text-sm" data-testid={`photo-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {it.present
                  ? <Icons.CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                  : <Icons.Circle size={16} className="text-muted-foreground/60 shrink-0" />}
                <span className={it.present ? "" : "text-muted-foreground"}>{it.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Job Pack */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-job-pack">
        <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.ClipboardList size={16} className="text-secondary" /> Job Pack</h3>
        {signedQuote ? (
          <>
            <p className="text-xs text-muted-foreground mb-3">Installer document — customer (no phone/address, suburb only), full property details, signed-quote products and installation notes.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setJobPackMode("download"); setJobPackOpen(true); }}
                data-testid="job-pack-download" className="px-4 py-2 rounded-md border border-border text-sm font-semibold flex items-center gap-1.5"><Icons.Download size={14} /> Download</button>
              {editable && (
                <button onClick={() => { setJobPackMode("send"); setJobPackOpen(true); }} data-testid="job-pack-send-open" className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-1.5"><Icons.Send size={14} /> Send Job Pack</button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="job-pack-unavailable">Available once the customer has signed a quote.</p>
        )}
      </div>
      {jobPackOpen && <JobPackDialog open={jobPackOpen} setOpen={setJobPackOpen} lead={lead} mode={jobPackMode} onSent={onRefresh} />}

      <fieldset disabled={saving} className="space-y-4 min-w-0" data-testid="prepost-edit-fields">
      {/* DNSP precedes Solar Victoria */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-dnsp">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Icons.Zap size={16} className="text-secondary" /> Grid Pre-Approval (DNSP)</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Pre-Approval Reference Number"><input value={f.dnsp_preapproval_ref} disabled={!canEdit} onChange={(e) => set("dnsp_preapproval_ref", e.target.value)} data-testid="prepost-dnsp-ref" className={inputCls} /></Field>
          <Field label="Approved Export Limit"><input value={f.dnsp_export_limit} disabled={!canEdit} onChange={(e) => set("dnsp_export_limit", e.target.value)} placeholder="e.g. 5kW" data-testid="prepost-dnsp-export" className={inputCls} /></Field>
        </div>
      </div>

      {/* Solar Victoria */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-solarvic">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Icons.Sun size={16} className="text-secondary" /> Solar Victoria Rebates</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Solar VIC Application Status">
            <select value={f.solar_vic_status} disabled={!canEdit}
              onChange={(e) => {
                const v = e.target.value;
                setF((s) => ({ ...s, solar_vic_status: v, ...(v === "Not eligible" ? { include_vic_rebate: false, include_vic_loan: false } : {}) }));
              }}
              data-testid="prepost-solar-vic-status" className={inputCls}>
              <option value="">— Select —</option>{SOLAR_VIC_STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          {!notEligible && (
            <Field label="Solar VIC INS No"><input value={f.solar_vic_ins_no} disabled={!canEdit} onChange={(e) => set("solar_vic_ins_no", e.target.value)} data-testid="prepost-solar-vic-ins" className={inputCls} /></Field>
          )}
        </div>
        <div className="mt-4 space-y-2 border-t border-border/50 pt-4">
          <p className="text-xs text-muted-foreground">These control the <b>Tax Invoice</b> calculation only — the quote and signed quote are never changed.</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!notEligible && f.include_vic_rebate} disabled={!canEdit || notEligible} onChange={(e) => set("include_vic_rebate", e.target.checked)} data-testid="prepost-include-vic-rebate" /> Include Solar Victoria Rebate</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!notEligible && f.include_vic_loan} disabled={!canEdit || notEligible} onChange={(e) => set("include_vic_loan", e.target.checked)} data-testid="prepost-include-vic-loan" /> Include Solar Victoria Loan</label>
          {notEligible && <p className="text-xs text-amber-700" data-testid="prepost-vic-not-eligible-note">Marked "Not eligible" — both rebate and loan are excluded from the tax invoice.</p>}
        </div>
        <div className="mt-4"><TickButton active={!!lead.solar_vic_completed} disabled={!canEdit || saving} testid="prepost-solar-vic-completed"
          onClick={() => save({ solar_vic_completed: !lead.solar_vic_completed })} label={lead.solar_vic_completed ? "Solar VIC Completed" : "Mark Solar VIC Completed"} /></div>
      </div>

      {/* Installation Scheduled */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-install">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Icons.CalendarClock size={16} className="text-secondary" /> Installation Scheduled</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Stock Delivery Booked"><input type="date" value={f.stock_delivery_booked} disabled={!canEdit} onChange={(e) => set("stock_delivery_booked", e.target.value)} data-testid="prepost-stock-delivery" className={inputCls} /></Field>
          <Field label="Installation Booked"><input type="date" value={f.install_booked_date} disabled={!canEdit} onChange={(e) => set("install_booked_date", e.target.value)} data-testid="prepost-install-booked" className={inputCls} /></Field>
        </div>
        <div className="mt-4">
          <div className="mb-3"><TickButton active={!!lead.installation_scheduled} disabled={!canEdit || saving || (!lead.installation_scheduled && !f.install_booked_date)} testid="prepost-install-scheduled"
            onClick={() => save({ installation_scheduled: !lead.installation_scheduled })} label={lead.installation_scheduled ? "Installation Scheduled" : "Mark Installation Scheduled"} /></div>
          {!f.install_booked_date && !lead.installation_scheduled && <p className="text-xs text-muted-foreground mb-3" data-testid="prepost-scheduled-date-required">Enter the installation booked date to mark it scheduled.</p>}
          <TickButton active={!!lead.installation_completed} disabled={!canEdit || saving || !!lead.installation_completed} testid="prepost-install-completed"
            onClick={() => save({ installation_completed: true })} label={lead.installation_completed ? "Installation Completed" : "Mark Installation Completed"} />
          <p className="text-xs text-muted-foreground mt-1.5">Upload the Certificate of Electrical Safety (CES) in the Documents section.</p>
        </div>
      </div>

      {/* Metering & Retailer */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-metering">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Icons.Gauge size={16} className="text-secondary" /> Metering &amp; Retailer Notification</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Distributor Post Installation Application">
            <select value={f.distributor_post_install_status} disabled={!canEdit} onChange={(e) => set("distributor_post_install_status", e.target.value)} data-testid="prepost-distributor-status" className={inputCls}>
              <option value="">— Select —</option>{DISTRIBUTOR_POST_INSTALL.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Retailer Notified">
            <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={f.retailer_notified} disabled={!canEdit} onChange={(e) => set("retailer_notified", e.target.checked)} data-testid="prepost-retailer-notified" /> Yes, retailer has been notified</label>
          </Field>
          {f.distributor_post_install_status === "Submitted" && (
            <Field label="Post Installation Reference Number"><input value={f.post_install_ref} disabled={!canEdit} onChange={(e) => set("post_install_ref", e.target.value)} placeholder="e.g. CA22RLQ2K4VUA" data-testid="prepost-postinstall-ref" className={inputCls} /></Field>
          )}
        </div>
        <div className="mt-4">
          <TickButton active={!!lead.post_install_completed} disabled={!canEdit || !!lead.post_install_completed} testid="prepost-postinstall-completed"
            onClick={() => save({ post_install_completed: true })} label={lead.post_install_completed ? "Post Installation Completed" : "Mark Post Installation Completed"} />
        </div>
      </div>

      {canEdit && (
        <div className="flex flex-wrap gap-3 items-center">
          <button onClick={() => save()} disabled={saving} data-testid="prepost-save" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save Prepost Details"}</button>
          {!lead.won_finalized && (
            <button onClick={() => { if (window.confirm("Mark this project Won — Finalized? It will lock for Sales and move to the Won - Finalized stage.")) save({ won_finalized: true }); }}
              disabled={saving} data-testid="prepost-won-finalized" className="px-5 py-2 rounded-md bg-emerald-600 text-white font-semibold disabled:opacity-60 flex items-center gap-2"><Icons.BadgeCheck size={16} /> Mark Won — Finalized</button>
          )}
        </div>
      )}
      </fieldset>

      <button onClick={onOpenChat} data-testid="prepost-open-lead-chat" className="inline-flex gap-2 items-center rounded-md border border-border bg-card px-4 py-3 text-sm font-semibold text-secondary hover:bg-muted"><Icons.MessagesSquare size={17} />Open lead chat</button>

      {/* Tax Invoice lives under Prepost too */}
      <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="prepost-tax-invoice">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Icons.Receipt size={16} className="text-secondary" /> Tax Invoice</h3>
        <TaxInvoicePanel leadId={lead.id} lead={lead} />
      </div>
    </div>
  );
}

function JobPackDialog({ open, setOpen, lead, mode = "send", onSent }) {
  const isDownload = mode === "download";
  const [email, setEmail] = useState("info@griansolarpower.com.au");
  const [contacts, setContacts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/leads/${lead.id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    if (!isDownload) {
      api.get(`/job-pack/contacts`).then((r) => {
        setContacts(r.data.contacts || []);
        setEmail(r.data.default_email || "info@griansolarpower.com.au");
      }).catch(() => {});
    }
  }, [open, lead.id, isDownload]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async () => {
    setBusy(true);
    try {
      if (isDownload) {
        await postUrl(`/leads/${lead.id}/job-pack/combined.pdf`, { document_ids: selected },
          { download: true, filename: `Job-Pack-${lead.uid}.pdf` });
        toast.success("Job pack downloaded");
      } else {
        if (!email.trim()) { setBusy(false); return toast.error("Enter a recipient email"); }
        await api.post(`/leads/${lead.id}/send-job-pack`, { email: email.trim(), document_ids: selected });
        toast.success(`Job pack sent to ${email.trim()}`);
      }
      setOpen(false); onSent?.();
    } catch (e) { toast.error(errMsg(e, "Could not build the job pack")); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg" data-testid="job-pack-dialog">
        <DialogHeader><DialogTitle>{isDownload ? "Download Job Pack" : "Send Job Pack"}</DialogTitle>
          <DialogDescription>{isDownload
            ? "Choose which documents to include. Everything is merged into one PDF — the cover (customer + site details), then each photo or document on its own labelled page."
            : "Emails one merged PDF — the cover (customer + site details) followed by each document you attach, every page labelled."}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {!isDownload && (
            <>
              <label className="block text-sm">Recipient email
                <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="job-pack-email"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" /></label>
              {contacts.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="job-pack-contact-picks">
                  {contacts.map((c, i) => (
                    <button type="button" key={i} data-testid={`job-pack-contact-pick-${i}`} onClick={() => setEmail(c.email)}
                      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${email === c.email ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                      {c.name ? `${c.name} · ${c.email}` : c.email}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <div>
            <div className="label-caps mb-1">Include documents</div>
            {docs.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="job-pack-no-docs">No uploaded documents on this lead.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border/50" data-testid="job-pack-docs">
                {docs.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                    <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} data-testid={`job-pack-doc-${d.id}`} />
                    <Icons.FileText size={14} className="text-muted-foreground shrink-0" />
                    <span className="[overflow-wrap:anywhere]">{d.label || d.filename}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {selected.length === 0 && (
            <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="job-pack-empty-note">
              <Icons.Info size={13} className="mt-0.5 shrink-0" />
              No documents selected — the Job Pack will contain the cover page only.
            </p>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-border text-sm font-semibold">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="job-pack-send-confirm"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">
            {busy ? (isDownload ? "Building…" : "Sending…") : (isDownload ? "Download" : "Send Job Pack")}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

